/*
 * Sakura K hybrid collection loading.
 * Shopify's crawlable ?page=n links remain in Liquid; this script only enhances
 * the customer-facing experience by appending the next page to the current grid.
 */
(() => {
  if (window.SakuraInfiniteCollection) return;

  class SakuraInfiniteCollection {
    constructor() {
      this.container = document.getElementById('ProductGridContainer');
      if (!this.container) return;

      this.loading = false;
      this.generation = 0;
      this.abortController = null;
      this.intersectionObserver = null;
      this.directChildObserver = new MutationObserver((mutations) => {
        if (mutations.some((mutation) => mutation.target === this.container)) this.reset();
      });
      this.directChildObserver.observe(this.container, { childList: true });

      this.reset();
      this.rememberScrollPosition();
    }

    reset() {
      this.generation += 1;
      this.loading = false;
      if (this.abortController) this.abortController.abort();
      if (this.intersectionObserver) this.intersectionObserver.disconnect();

      this.control = this.container.querySelector('[data-sakura-infinite]');
      this.grid = this.container.querySelector('#product-grid');
      if (!this.control || !this.grid) return;
      this.control.classList.add('is-enhanced');

      this.status = this.control.querySelector('[data-sakura-infinite-status]');
      this.fallbackButton = this.control.querySelector('[data-sakura-infinite-button]');
      this.nextUrl = this.control.dataset.nextUrl || '';
      this.currentPage = Number(this.control.dataset.currentPage || 1);

      if (this.fallbackButton) {
        this.fallbackButton.hidden = true;
        this.fallbackButton.onclick = () => this.loadNextPage();
      }

      if (!this.nextUrl) {
        this.markComplete();
        return;
      }

      if (!('IntersectionObserver' in window)) {
        this.showFallback();
        return;
      }

      this.intersectionObserver = new IntersectionObserver(
        (entries) => {
          if (entries.some((entry) => entry.isIntersecting)) this.loadNextPage();
        },
        { rootMargin: '900px 0px 900px 0px' },
      );
      this.intersectionObserver.observe(this.control);
    }

    setStatus(message, loading = false) {
      if (!this.status) return;
      this.status.textContent = message;
      this.status.classList.toggle('is-loading', loading);
    }

    showFallback() {
      this.setStatus('More styles are ready to view.');
      if (this.fallbackButton) this.fallbackButton.hidden = false;
    }

    markComplete() {
      if (this.intersectionObserver) this.intersectionObserver.disconnect();
      this.setStatus('You have viewed all styles.');
      if (this.fallbackButton) this.fallbackButton.hidden = true;
      if (this.control) this.control.classList.add('is-complete');
    }

    async loadNextPage() {
      if (this.loading || !this.nextUrl || !this.grid) return;
      this.loading = true;
      const generation = this.generation;
      this.abortController = new AbortController();
      this.setStatus('Loading more styles…', true);
      if (this.fallbackButton) this.fallbackButton.hidden = true;

      try {
        const next = new URL(this.nextUrl, window.location.origin);
        const response = await fetch(next.href, {
          headers: { 'X-Requested-With': 'XMLHttpRequest' },
          signal: this.abortController.signal,
        });
        if (!response.ok) throw new Error(`Collection page returned ${response.status}`);

        const documentFromNextPage = new DOMParser().parseFromString(await response.text(), 'text/html');
        const nextGrid = documentFromNextPage.querySelector('#ProductGridContainer #product-grid');
        const nextControl = documentFromNextPage.querySelector(
          '#ProductGridContainer [data-sakura-infinite]',
        );
        if (!nextGrid || !nextControl) throw new Error('Next collection page markup is incomplete');
        if (generation !== this.generation) return;

        const fragment = document.createDocumentFragment();
        nextGrid.querySelectorAll(':scope > .grid__item').forEach((item) => {
          item.classList.add('scroll-trigger--cancel');
          fragment.appendChild(item);
        });
        this.grid.appendChild(fragment);

        this.nextUrl = nextControl.dataset.nextUrl || '';
        this.currentPage = Number(nextControl.dataset.currentPage || this.currentPage + 1);
        this.control.dataset.nextUrl = this.nextUrl;
        this.control.dataset.currentPage = String(this.currentPage);
        this.updateAddress(next);

        if (this.nextUrl) this.setStatus('Scroll to view more.');
        else this.markComplete();
      } catch (error) {
        if (error.name !== 'AbortError') {
          console.warn('[Sakura collection] Automatic loading paused:', error);
          this.showFallback();
        }
      } finally {
        if (generation === this.generation) this.loading = false;
      }
    }

    updateAddress(nextUrl) {
      const state = { ...(window.history.state || {}), sakuraInfinitePage: this.currentPage };
      window.history.replaceState(state, '', `${nextUrl.pathname}${nextUrl.search}`);
    }

    rememberScrollPosition() {
      this.container.addEventListener('click', (event) => {
        const productLink = event.target.closest('#product-grid a[href*="/products/"]');
        if (!productLink) return;
        try {
          sessionStorage.setItem(
            `sakura-collection-scroll:${window.location.pathname}`,
            JSON.stringify({ url: window.location.href, y: window.scrollY, savedAt: Date.now() }),
          );
        } catch (_) {
          // Storage can be unavailable in private browsing; native history still works.
        }
      });
    }
  }

  const start = () => {
    window.SakuraInfiniteCollection = new SakuraInfiniteCollection();
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once: true });
  else start();
})();
